import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'slices';
import { createBankAccount, updateBankAccount, clearPaymentState } from 'slices/payments/paymentSlice';
import { fetchPaymentGateways, updateGatewayStatus } from 'slices/paymentGateways/paymentGatewaysSlice';
import api from 'helpers/api_helper';
import QRCode from 'qrcode';

// Import payment gateway icons
import razorpayIcon from '../../assets/images/svg/payments-icons/razorpay-icon.webp';
import phonePayIcon from '../../assets/images/svg/payments-icons/PhonePay.svg';
import cashfreeIcon from '../../assets/images/svg/payments-icons/Cashfree Payments.jpg';
import payuIcon from '../../assets/images/svg/payments-icons/PayU.svg.png';

type TabType = 'imps' | 'upi' | 'qr';
type ViewType = 'manual' | 'api';

const PaymentsPage: React.FC = () => {
  const location = useLocation();
  const viewType: ViewType = location.pathname.includes('/api') ? 'api' : 'manual';
  const dispatch = useDispatch<AppDispatch>();
  const { creatingBank, createBankError, lastCreatedBank, message } = useSelector((s: RootState) => s.Payments);
  const { gateways: apiGateways, loading: gatewaysLoading, error: gatewaysError, updatingGateway, updateError } = useSelector((s: RootState) => s.PaymentGateways);
  const [lastPostContext, setLastPostContext] = useState<'imps' | 'upi' | null>(null);
  
  const [activeTab, setActiveTab] = useState<TabType>('imps');
  
  // QR tab states
  const [upiId, setUpiId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [qrUtr, setQrUtr] = useState('');

  // IMPS (Banking Details) states
  const [impsData, setImpsData] = useState({
    accountName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    utr: ''
  });
  const [impsScreenshot, setImpsScreenshot] = useState<File | null>(null);

  // UPI states
  const [upiData, setUpiData] = useState({
    accountName: '',
    upiHandle: '',
    phoneNo: '',
    utr: ''
  });
  const [upiScreenshot, setUpiScreenshot] = useState<File | null>(null);
  const [isActiveImps, setIsActiveImps] = useState<boolean>(true);
  const [isActiveUpi, setIsActiveUpi] = useState<boolean>(true);
  const [isActiveQr, setIsActiveQr] = useState<boolean>(true);

  // Generate a real QR code image (data URL) using qrcode lib
  const generateQRCode = async (text: string) => {
    try {
      const dataUrl = await QRCode.toDataURL(text, {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: 192,
        color: { dark: '#000000', light: '#FFFFFFFF' },
      });
      setQrCodeDataUrl(dataUrl);
    } catch (err) {
      // Fallback to empty on error
      setQrCodeDataUrl('');
    }
  };

  useEffect(() => {
    const payload = upiId
      ? `upi://pay?pa=${encodeURIComponent(upiId)}`
      : 'https://sattamatkaapi.com/payment';
    generateQRCode(payload);
  }, [upiId]);

  // Fetch payment gateways when API view is active
  useEffect(() => {
    if (viewType === 'api') {
      dispatch(fetchPaymentGateways());
    }
  }, [viewType, dispatch]);

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (viewType === 'manual' && activeTab === 'imps') {
      // If admin set inactive and a bank is selected, soft delete
      if (!isActiveImps && selectedBankId) {
        api.delete(`/admin/banks/${selectedBankId}/`).catch(() => {});
        return;
      }
      // Validate IMPS fields
      if (!validateAccountName(impsData.accountName) || !validateBankName(impsData.bankName) || 
          !validateAccountNumber(impsData.accountNumber) || !validateIFSC(impsData.ifscCode)) {
        alert('Please fill all required fields correctly');
        return;
      }
      if (selectedBankId && !addBankMode) {
        dispatch(updateBankAccount({
          id: selectedBankId,
          payload: {
            name: impsData.bankName,
            account_name: impsData.accountName,
            account_number: impsData.accountNumber,
            ifsc_code: impsData.ifscCode,
            status: isActiveImps ? 'active' : 'inactive'
          }
        }));
      } else {
        dispatch(createBankAccount({
          name: impsData.bankName,
          account_name: impsData.accountName,
          account_number: impsData.accountNumber,
          ifsc_code: impsData.ifscCode,
          status: isActiveImps ? 'active' : 'inactive'
        }));
        setAddBankMode(false);
      }
      setLastPostContext('imps');
    } else if (viewType === 'manual' && activeTab === 'upi') {
      if (!isActiveUpi && selectedBankId) {
        api.delete(`/admin/banks/${selectedBankId}/`).catch(() => {});
        return;
      }
      // Validate UPI fields
      if (!validateAccountName(upiData.accountName) || !validateUPIHandle(upiData.upiHandle) || 
          !validatePhoneNumber(upiData.phoneNo)) {
        alert('Please fill all required fields correctly');
        return;
      }
      if (selectedBankId && !addBankMode) {
        dispatch(updateBankAccount({
          id: selectedBankId,
          payload: {
            account_name: upiData.accountName,
            upi_handle: upiData.upiHandle,
            phone_number: upiData.phoneNo,
            status: isActiveUpi ? 'active' : 'inactive'
          }
        }));
      } else {
        dispatch(createBankAccount({
          account_name: upiData.accountName,
          upi_handle: upiData.upiHandle,
          phone_number: upiData.phoneNo,
          status: isActiveUpi ? 'active' : 'inactive'
        }));
        setAddBankMode(false);
      }
      setLastPostContext('upi');
    } else if (viewType === 'manual' && activeTab === 'qr') {
      // Validate selected file
      if (!file) {
        alert('Please select a QR code image');
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Invalid file type. Only JPEG, PNG, WebP are allowed.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File too large. Maximum size is 5MB.');
        return;
      }

      (async () => {
        try {
          const fd = new FormData();
          fd.append('qr_code', file);
          fd.append('status', isActiveQr ? 'active' : 'inactive');

          if (selectedBankId && !addBankMode) {
            // Update QR only for existing bank
            await api.put(`/admin/banks/${selectedBankId}/`, fd);
            alert('QR code updated successfully.');
          } else {
            // Create new bank with just QR code (no IMPS fields required)
            // Auto-generate a descriptive name for QR bank
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD format
            const timeStr = now.toTimeString().split(' ')[0].substring(0, 5); // HH:MM format
            const qrBankName = `QR Bank - ${dateStr} ${timeStr}`;
            fd.append('name', qrBankName);
            
            const createRes = await api.post('/admin/banks/', fd);
            const newId = createRes?.data?.bank?.id;
            if (newId) {
              setSelectedBankId(Number(newId));
              // Refresh banks list to show the new bank with auto-generated name
              const refreshRes = await api.get('/admin/banks/');
              const refreshRaw = refreshRes?.data;
              const refreshList: any[] = Array.isArray(refreshRaw)
                ? refreshRaw
                : Array.isArray(refreshRaw?.banks)
                  ? refreshRaw.banks
                  : Array.isArray(refreshRaw?.data)
                    ? refreshRaw.data
                    : [];
              const refreshMapped = refreshList
                .map((b: any) => ({ 
                  id: Number(b.id), 
                  name: String(b.name || b.bank_name || 'Unnamed Bank'),
                  qr_code: b.qr_code || b.qr_code_url || null,
                  upi_handle: b.upi_handle || null,
                  account_number: b.account_number || null,
                  ifsc_code: b.ifsc_code || null
                }))
                .filter((b: any) => b.id && b.name);
              setBanks(refreshMapped);
            }
            setAddBankMode(false);
            alert('QR code saved successfully.');
          }
        } catch (err: any) {
          alert(err?.response?.data?.message || 'Failed to save QR code');
        }
      })();
    }
  };

  const handleImpsChange = (field: string, value: string) => {
    let processedValue = value;

    if (field === 'accountName' || field === 'bankName') {
      // Allow only letters, spaces, and dots, max 50 chars
      processedValue = value.replace(/[^A-Za-z .]/g, '').substring(0, 50);
    }

    setImpsData(prev => ({ ...prev, [field]: processedValue }));
  };

  const handleUpiChange = (field: string, value: string) => {
    let processedValue = value;

    if (field === 'phoneNo') {
      // Only digits, max 10 chars
      processedValue = value.replace(/\D/g, '').substring(0, 10);
    } else if (field === 'upiHandle') {
      // Allow alphanumeric, dots, underscores, hyphens, and @, max 60 chars
      processedValue = value.replace(/[^a-zA-Z0-9._@-]/g, '').substring(0, 60);
    } else if (field === 'accountName') {
      // Allow only letters, spaces, and dots, max 50 chars
      processedValue = value.replace(/[^A-Za-z .]/g, '').substring(0, 50);
    }

    setUpiData(prev => ({ ...prev, [field]: processedValue }));
  };

  const handleGatewayToggle = async (gatewayCode: string) => {
    // Find the gateway to get current status
    const gateway = apiGateways.find(g => g.code === gatewayCode);
    if (!gateway) return;
    
    // Toggle status: active -> inactive, inactive -> active
    const newStatus = gateway.is_active ? 'inactive' : 'active';
    
    try {
      await dispatch(updateGatewayStatus({
        code: gatewayCode,
        status: newStatus
      })).unwrap();
    } catch (error) {
      console.error('Failed to update gateway status:', error);
    }
  };

  // Simple validations - only check on submit
  const validateAccountName = (value: string) => {
    return value.length >= 3 && value.length <= 50 && /^[A-Za-z][A-Za-z .]*$/.test(value);
  };

  const validateBankName = (value: string) => {
    return value.length >= 1 && value.length <= 50 && /^[A-Za-z][A-Za-z .]*$/.test(value);
  };

  const validateAccountNumber = (value: string) => {
    return value.length >= 6 && value.length <= 18 && /^\d{6,18}$/.test(value);
  };

  const validateIFSC = (value: string) => {
    return value.length >= 10 && value.length <= 11 && /^[A-Z]{4}0[A-Z0-9]{5,6}$/.test(value);
  };

  const validateUPIHandle = (value: string) => {
    return value.length > 0 && value.length <= 60 && /^[a-zA-Z0-9._-]{2,}@[a-zA-Z0-9]{2,}$/.test(value);
  };

  const validatePhoneNumber = (value: string) => {
    return value.length === 10 && /^\d{10}$/.test(value);
  };

  const handleImpsInputChange = (field: string, value: string) => {
    let processedValue = value;

    if (field === 'accountNumber') {
      // Only digits, max 18 chars
      processedValue = value.replace(/\D/g, '').substring(0, 18);
    } else if (field === 'ifscCode') {
      // Uppercase, alphanumeric only, max 11 chars
      processedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 11);
    }
    
    setImpsData(prev => ({ ...prev, [field]: processedValue }));
  };

  // Banks dropdown state and fetch
  const [banks, setBanks] = useState<Array<{ id: number; name: string; qr_code?: string | null; upi_handle?: string | null; account_number?: string | null; ifsc_code?: string | null }>>([]);
  const [selectedBankId, setSelectedBankId] = useState<number | ''>('');
  const [bankLoading, setBankLoading] = useState<boolean>(false);
  const [addBankMode, setAddBankMode] = useState<boolean>(false);
  // Track which bank was last fetched for each tab to prevent cross-tab data leakage
  const [lastFetchedBankForTab, setLastFetchedBankForTab] = useState<{ [key: string]: number | '' }>({
    imps: '',
    upi: ''
  });
  // Track whether the bank selection was changed by the user (kept for compatibility)
  const [bankChangedByUser, setBankChangedByUser] = useState<boolean>(false);

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const res = await api.get('/admin/banks/');
        const raw = res?.data;
        // Try common shapes: {banks: []} or []
        const list: any[] = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.banks)
            ? raw.banks
            : Array.isArray(raw?.data)
              ? raw.data
              : [];
        const mapped = list
          .map((b: any) => ({ 
            id: Number(b.id), 
            name: String(b.name || b.bank_name || 'Unnamed Bank'),
            qr_code: b.qr_code || b.qr_code_url || null,
            upi_handle: b.upi_handle || null,
            account_number: b.account_number || null,
            ifsc_code: b.ifsc_code || null
          }))
          .filter((b: any) => b.id && b.name);
        setBanks(mapped);
        if (!addBankMode && mapped.length > 0) setSelectedBankId(mapped[0].id);
      } catch (e) {
        // silently ignore for now; dropdown will remain empty
      }
    };
    fetchBanks();
  }, [addBankMode]);

  // Fetch details and auto-fill ONLY the current tab's fields when:
  // - a bank is selected OR
  // - the user switches tabs and the current tab hasn't fetched this bank yet
  useEffect(() => {
    const tabKey = activeTab as 'imps' | 'upi';
    const shouldFetch = viewType === 'manual' && !!selectedBankId && (lastFetchedBankForTab[tabKey] !== selectedBankId);
    if (!shouldFetch) return;

    let cancelled = false;
    const run = async () => {
      try {
        setBankLoading(true);
        const res = await api.get(`/admin/banks/${selectedBankId}/`);
        const raw = res?.data;
        const bank: any = raw?.bank ?? raw?.data ?? raw;
        if (!bank || cancelled) return;

        if (activeTab === 'imps') {
          setImpsData(prev => ({
            accountName: bank.account_name ?? prev.accountName ?? '',
            bankName: bank.name ?? prev.bankName ?? '',
            accountNumber: bank.account_number ?? prev.accountNumber ?? '',
            ifscCode: bank.ifsc_code ?? prev.ifscCode ?? '',
            utr: prev.utr ?? ''
          }));
          setLastFetchedBankForTab(prev => ({ ...prev, imps: selectedBankId as number }));
          setIsActiveImps(typeof bank.status === 'string' ? bank.status.toLowerCase() === 'active' : true);
        } else if (activeTab === 'upi') {
          setUpiData(prev => ({
            accountName: bank.account_name ?? prev.accountName ?? '',
            upiHandle: bank.upi_handle ?? prev.upiHandle ?? '',
            phoneNo: bank.phone_number ?? prev.phoneNo ?? '',
            utr: prev.utr ?? ''
          }));
          setLastFetchedBankForTab(prev => ({ ...prev, upi: selectedBankId as number }));
          setIsActiveUpi(typeof bank.status === 'string' ? bank.status.toLowerCase() === 'active' : true);
        } else if (activeTab === 'qr') {
          setIsActiveQr(typeof bank.status === 'string' ? bank.status.toLowerCase() === 'active' : true);
        }
      } catch (e) {
        // ignore errors; keep user input intact
      } finally {
        if (!cancelled) setBankLoading(false);
      }
    };
    run();
    setBankChangedByUser(false);
    return () => { cancelled = true; };
  }, [selectedBankId, viewType, activeTab, lastFetchedBankForTab]);

  // After a successful POST (IMPS or UPI), fetch the just-created/updated bank by id
  // and reflect the latest values back ONLY in the initiating tab's form.
  useEffect(() => {
    if (!lastCreatedBank?.id || viewType !== 'manual') return;
    // Only refetch for the tab that initiated the POST
    if (lastPostContext !== 'imps' && lastPostContext !== 'upi') return;
    if (activeTab !== lastPostContext) return;

    let cancelled = false;
    const run = async () => {
      try {
        setBankLoading(true);
        const res = await api.get(`/admin/banks/${lastCreatedBank.id}/`);
        const raw = res?.data;
        const bank: any = raw?.bank ?? raw?.data ?? raw;
        if (!bank || cancelled) return;

        if (lastPostContext === 'imps') {
          setImpsData(prev => ({
            accountName: bank.account_name ?? prev.accountName ?? '',
            bankName: bank.name ?? prev.bankName ?? '',
            accountNumber: bank.account_number ?? prev.accountNumber ?? '',
            ifscCode: bank.ifsc_code ?? prev.ifscCode ?? '',
            utr: prev.utr ?? ''
          }));
          setLastFetchedBankForTab(prev => ({ ...prev, imps: bank.id }));
          setIsActiveImps(typeof bank.status === 'string' ? bank.status.toLowerCase() === 'active' : true);
        } else if (lastPostContext === 'upi') {
          setUpiData(prev => ({
            accountName: bank.account_name ?? prev.accountName ?? '',
            upiHandle: bank.upi_handle ?? prev.upiHandle ?? '',
            phoneNo: bank.phone_number ?? prev.phoneNo ?? '',
            utr: prev.utr ?? ''
          }));
          setLastFetchedBankForTab(prev => ({ ...prev, upi: bank.id }));
          setIsActiveUpi(typeof bank.status === 'string' ? bank.status.toLowerCase() === 'active' : true);
        }
        // If we were in add mode and a bank got created, add it to dropdown and select it
        if (addBankMode && bank?.id && bank?.name) {
          const exists = banks.some(b => b.id === Number(bank.id));
          if (!exists) setBanks(prev => [{ 
            id: Number(bank.id), 
            name: String(bank.name),
            qr_code: bank.qr_code || bank.qr_code_url || null,
            upi_handle: bank.upi_handle || null,
            account_number: bank.account_number || null,
            ifsc_code: bank.ifsc_code || null
          }, ...prev]);
          setSelectedBankId(Number(bank.id));
          setAddBankMode(false);
        }
      } catch (e) {
        // ignore
      } finally {
        if (!cancelled) setBankLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [lastCreatedBank?.id, viewType, activeTab, lastPostContext]);

  return (
    <div className="p-6">
      {/* Bank selector above the payment settings card - Only show for Manual view */}
      {viewType === 'manual' && (
        <div className="bg-white rounded-lg shadow-sm border mb-4">
          <div className="p-6">
            <div className="flex items-center justify-between gap-3 mb-2">
              <label className="block text-sm font-medium text-gray-700">Bank Name</label>
              <button
                type="button"
                onClick={() => {
                  setAddBankMode(true);
                  setSelectedBankId('');
                  setLastFetchedBankForTab({ imps: '', upi: '' }); // Clear tracking for fresh form
                  setImpsData({ accountName: '', bankName: '', accountNumber: '', ifscCode: '', utr: '' });
                  setUpiData({ accountName: '', upiHandle: '', phoneNo: '', utr: '' });
                  setFile(null);
                  setUpiId('');
                  setQrUtr('');
                  setIsActiveImps(true);
                  setIsActiveUpi(true);
                  setIsActiveQr(true);
                  dispatch(clearPaymentState());
                }}
                className="inline-flex items-center px-3 py-1.5 text-sm rounded border bg-blue-600 text-white hover:bg-blue-700"
              >
                Add Bank +
              </button>
            </div>
            <select
              className="w-full max-w-md border rounded px-3 py-2"
              value={selectedBankId}
              onChange={(e) => {
                setSelectedBankId(e.target.value ? Number(e.target.value) : '');
                setBankChangedByUser(true);
              }}
            >
              {banks.length === 0 && <option value="">No banks available</option>}
              {banks.map((b) => {
                // Determine bank type indicators
                const indicators: string[] = [];
                if (b.qr_code) indicators.push('QR');
                if (b.upi_handle) indicators.push('UPI');
                if (b.account_number && b.account_number !== 'N/A' && b.ifsc_code && b.ifsc_code !== 'N/A') {
                  indicators.push('IMPS');
                }
                const indicatorStr = indicators.length > 0 ? ` [${indicators.join(', ')}]` : '';
                return (
                  <option key={b.id} value={b.id}>{b.name}{indicatorStr}</option>
                );
              })}
            </select>
            {bankLoading && (
              <p className="text-xs text-gray-500 mt-2">Loading bank details…</p>
            )}
          </div>
        </div>
      )}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold mb-2">
            {viewType === 'api' ? 'API Payment Gateways' : 'Manual Payment Settings'}
          </h2>
          {message && lastCreatedBank && (
            <div className="mt-2 text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
              {message} (ID: {lastCreatedBank.id})
            </div>
          )}
          {createBankError && (
            <div className="mt-2 text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {createBankError}
            </div>
          )}
          
          {/* Manual Tabs - Only show for manual view */}
          {viewType === 'manual' && (
          <>
          <div className="flex gap-0 border-b border-gray-200 -mb-6">
            <button
              type="button"
              onClick={() => setActiveTab('imps')}
              className={`px-6 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'imps'
                  ? 'text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Manual Deposit IMPS
              {activeTab === 'imps' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
              )}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('upi')}
              className={`px-6 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'upi'
                  ? 'text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Manual Deposit UPI
              {activeTab === 'upi' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
              )}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('qr')}
              className={`px-6 py-3 font-medium text-sm transition-colors relative ${
                activeTab === 'qr'
                  ? 'text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Manual Deposit QR
              {activeTab === 'qr' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
              )}
            </button>
          </div>
          </>
          )}
        </div>

        {/* Forms based on active tab */}
        {viewType === 'manual' && (
        <form className="p-6" onSubmit={handleUpdate}>
          {/* Manual Deposit IMPS Form */}
          {activeTab === 'imps' && (
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Active</label>
                <button
                  type="button"
                  onClick={() => {
                    const newActive = !isActiveImps;
                    setIsActiveImps(newActive);
                    if (!newActive && selectedBankId) {
                      // Soft delete immediately when unchecked
                      api.delete(`/admin/banks/${selectedBankId}/`).catch(() => {});
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isActiveImps ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isActiveImps ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {(!isActiveImps && selectedBankId) && (
                <p className="text-xs text-red-600">Unchecking Active will mark the selected bank as inactive.</p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Name
                </label>
                <input
                  type="text"
                  value={impsData.accountName}
                  onChange={(e) => handleImpsChange('accountName', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Enter account name"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank Name
                </label>
                <input
                  type="text"
                  value={impsData.bankName}
                  onChange={(e) => handleImpsChange('bankName', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Enter bank name"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Number
                </label>
                <input
                  type="text"
                  value={impsData.accountNumber}
                  onChange={(e) => handleImpsInputChange('accountNumber', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Enter account number (6-18 digits)"
                  maxLength={18}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IFSC Code
                </label>
                <input
                  type="text"
                  value={impsData.ifscCode}
                  onChange={(e) => handleImpsInputChange('ifscCode', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Enter IFSC code (e.g., SBIN0XXXXXX)"
                  maxLength={11}
                />
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UTR
                </label>
                <p className="text-sm text-gray-500 mb-2">Enter the Transaction Reference</p>
                <input
                  type="text"
                  value={impsData.utr}
                  onChange={(e) => handleImpsChange('utr', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Enter UTR"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Screenshot
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImpsScreenshot(e.target.files?.[0] || null)}
                  className="w-full border rounded px-3 py-2"
                />
                {impsScreenshot && (
                  <p className="text-sm text-gray-600 mt-2">Selected: {impsScreenshot.name}</p>
                )}
              </div> */}
              <div className="mt-6">
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60" disabled={creatingBank}>
                  {creatingBank ? 'Saving...' : 'Update'}
                </button>
              </div>
            </div>
          )}

          {/* Manual Deposit UPI Form */}
          {activeTab === 'upi' && (
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Active</label>
                <button
                  type="button"
                  onClick={() => {
                    const newActive = !isActiveUpi;
                    setIsActiveUpi(newActive);
                    if (!newActive && selectedBankId) {
                      // Soft delete immediately when unchecked
                      api.delete(`/admin/banks/${selectedBankId}/`).catch(() => {});
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isActiveUpi ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isActiveUpi ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {(!isActiveUpi && selectedBankId) && (
                <p className="text-xs text-red-600">Unchecking Active will mark the selected bank as inactive.</p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Name
                </label>
                <input
                  type="text"
                  value={upiData.accountName}
                  onChange={(e) => handleUpiChange('accountName', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Enter account name"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UPI Handle
                </label>
                <input
                  type="text"
                  value={upiData.upiHandle}
                  onChange={(e) => handleUpiChange('upiHandle', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Enter UPI handle (e.g., name@paytm)"
                  maxLength={60}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone No.
                </label>
                <input
                  type="text"
                  value={upiData.phoneNo}
                  onChange={(e) => handleUpiChange('phoneNo', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Enter phone number (10 digits)"
                  maxLength={10}
                />
                {upiData.phoneNo.length === 10 && (
                  <p className="text-xs text-green-600 mt-1">Valid phone number</p>
                )}
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UTR
                </label>
                <p className="text-sm text-gray-500 mb-2">Enter the Transaction Reference</p>
                <input
                  type="text"
                  value={upiData.utr}
                  onChange={(e) => handleUpiChange('utr', e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Enter UTR"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Screenshot
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setUpiScreenshot(e.target.files?.[0] || null)}
                  className="w-full border rounded px-3 py-2"
                />
                {upiScreenshot && (
                  <p className="text-sm text-gray-600 mt-2">Selected: {upiScreenshot.name}</p>
                )}
              </div> */}
              <div className="mt-6">
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                  Update
                </button>
              </div>
            </div>
          )}

          {/* Manual Deposit QR Form */}
          {activeTab === 'qr' && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Old QR Code</label>
                  <div className="w-48 h-48 border rounded flex items-center justify-center bg-gray-50">
                    {qrCodeDataUrl ? (
                      <img 
                        src={qrCodeDataUrl} 
                        alt="QR Code" 
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="text-gray-400 text-sm">Generating QR Code...</div>
                    )}
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700">Active</label>
                    <button
                      type="button"
                      onClick={() => {
                        const newActive = !isActiveQr;
                        setIsActiveQr(newActive);
                        if (!newActive && selectedBankId) {
                          // Soft delete immediately when unchecked
                          api.delete(`/admin/banks/${selectedBankId}/`).catch(() => {});
                        }
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        isActiveQr ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          isActiveQr ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  {(!isActiveQr && selectedBankId) && (
                    <p className="text-xs text-red-600">Unchecking Active will mark the selected bank as inactive.</p>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">QR Code</label>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => setFile(e.target.files?.[0] || null)} 
                      className="w-full border rounded px-3 py-2"
                    />
                    {file && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 mb-2">Preview:</p>
                        <img 
                          src={URL.createObjectURL(file)} 
                          alt="QR Code Preview" 
                          className="w-32 h-32 object-contain border rounded"
                        />
                      </div>
                    )}
                  </div>
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      UTR
                    </label>
                    <p className="text-sm text-gray-500 mb-2">Enter the Transaction Reference</p>
                    <input
                      type="text"
                      value={qrUtr}
                      onChange={(e) => setQrUtr(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                      placeholder="Enter UTR"
                    />
                  </div> */}
                </div>
              </div>
              <div className="mt-6">
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                  Update
                </button>
              </div>
            </>
          )}
        </form>
        )}

          {/* API Payment Gateways View */}
          {viewType === 'api' && (
            <div className="px-5 sm:px-6 py-3">
              {gatewaysLoading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading payment gateways...</p>
                </div>
              )}
              {gatewaysError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700">{gatewaysError}</p>
                </div>
              )}
              {updateError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700">{updateError}</p>
                </div>
              )}
              {!gatewaysLoading && !gatewaysError && (
                <>
                  <div className="space-y-2">
                    {apiGateways.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No payment gateways available</p>
                    ) : (
                      apiGateways.map((gateway) => (
                        <div key={gateway.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                          <div className="flex items-center gap-3">
                            <h3 className="font-medium text-gray-900 text-lg">{gateway.name}</h3>
                            <span className={`text-xs px-2 py-1 rounded ${
                              gateway.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {gateway.status}
                            </span>
                            {gateway.configured && (
                              <span className="text-xs px-2 py-1 rounded bg-orange-100 text-blue-800">
                                Configured
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleGatewayToggle(gateway.code)}
                            disabled={updatingGateway}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              gateway.is_active ? 'bg-blue-600' : 'bg-gray-300'
                            } ${updatingGateway ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                gateway.is_active ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="mt-4">
                    <button 
                      type="button" 
                      onClick={handleUpdate}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Save Changes
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default PaymentsPage;
